import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    this.#syncTaxonomyInputs();

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    this.#syncTaxonomyInputs();
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          const { taxonomyPath, catalogSlug } = this.#taxonomyContext;
          if (taxonomyPath) {
            searchUrl.searchParams.set('dzipmart_taxonomy', taxonomyPath);
          }
          if (catalogSlug) {
            searchUrl.searchParams.set('catalog', catalogSlug);
          }
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#syncTaxonomyInputs();
    this.#getSearchResults(searchTerm);
  }, 200);

  get #taxonomyContext() {
    const params = new URLSearchParams(window.location.search);
    const form = this.querySelector('form');
    const taxonomyInput = form?.querySelector('input[name="dzipmart_taxonomy"]');
    const catalogInput = form?.querySelector('input[name="catalog"]');
    const taxonomyPath = taxonomyInput instanceof HTMLInputElement ? taxonomyInput.value.trim() : params.get('dzipmart_taxonomy') || '';
    const catalogSlug = catalogInput instanceof HTMLInputElement
      ? catalogInput.value.trim()
      : params.get('catalog') || taxonomyPath.split('/').filter(Boolean).pop() || '';

    return {
      taxonomyPath,
      catalogSlug,
    };
  }

  #ensureHiddenInput(name, value) {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement) || !value) return;

    let input = form.querySelector(`input[name="${name}"]`);
    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.append(input);
    }

    input.value = value;
  }

  #syncTaxonomyInputs() {
    const { taxonomyPath, catalogSlug } = this.#taxonomyContext;

    if (taxonomyPath) {
      this.#ensureHiddenInput('dzipmart_taxonomy', taxonomyPath);
    }

    if (catalogSlug) {
      this.#ensureHiddenInput('catalog', catalogSlug);
    }
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    const { taxonomyPath, catalogSlug } = this.#taxonomyContext;
    if (taxonomyPath) {
      url.searchParams.set('dzipmart_taxonomy', taxonomyPath);
    }
    if (catalogSlug) {
      url.searchParams.set('catalog', catalogSlug);
    }

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();
    try {
      const [resultsMarkup, marketplacePayload] = await Promise.all([
        sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url),
        this.#getMarketplaceResults(searchTerm, abortController.signal),
      ]);

      if (!resultsMarkup || abortController.signal.aborted) return;

      morph(predictiveSearchResults, resultsMarkup);

      if (abortController.signal.aborted) return;

      this.#renderMarketplaceResults(marketplacePayload, searchTerm);
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  async #getMarketplaceResults(searchTerm, signal) {
    const endpoint = this.dataset.marketplaceSearchUrl;
    if (!endpoint || !searchTerm) return { hits: [], taxonomyContext: null };

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('per_page', '4');
      const { taxonomyPath, catalogSlug } = this.#taxonomyContext;
      if (taxonomyPath) {
        url.searchParams.set('dzipmart_taxonomy', taxonomyPath);
      }
      if (catalogSlug) {
        url.searchParams.set('catalog_slug', catalogSlug);
      }

      const response = await fetch(url.toString(), {
        signal,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return { hits: [], taxonomyContext: null };

      const payload = await response.json();
      return {
        hits: Array.isArray(payload?.hits) ? payload.hits : [],
        taxonomyContext: payload?.taxonomy_context || null,
      };
    } catch (error) {
      if (error?.name === 'AbortError') return { hits: [], taxonomyContext: null };
      return { hits: [], taxonomyContext: null };
    }
  }

  #renderMarketplaceResults(payload, searchTerm) {
    const existing = this.refs.predictiveSearchResults.querySelector('[data-marketplace-results]');
    existing?.remove();

    const results = Array.isArray(payload?.hits) ? payload.hits : [];
    const taxonomyContext = payload?.taxonomyContext || null;
    if (!Array.isArray(results) || results.length === 0) return;

    const inner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
    if (!inner) return;

    const noResults = inner.querySelector('.predictive-search-results__no-results');
    if (noResults instanceof HTMLElement) {
      noResults.hidden = true;
    }

    const wrapper = document.createElement('section');
    wrapper.className = 'marketplace-search-results predictive-search-results__wrapper';
    wrapper.dataset.marketplaceResults = 'true';

    const header = document.createElement('div');
    header.className = 'marketplace-search-results__header';

    const title = document.createElement('div');
    title.className = 'marketplace-search-results__title';
    title.textContent = taxonomyContext?.label ? `Marketplace matches in ${taxonomyContext.label}` : 'Marketplace matches';

    const meta = document.createElement('div');
    meta.className = 'marketplace-search-results__meta';
    meta.textContent = taxonomyContext?.path
      ? `Taxonomy-aware results for "${searchTerm}" in ${taxonomyContext.path}`
      : `Marketplace-aware results for "${searchTerm}"`;

    header.append(title, meta);
    wrapper.append(header);

    const list = document.createElement('ul');
    list.className = 'marketplace-search-results__list predictive-search-results__list list-unstyled';

    results.forEach((item) => {
      const linkUrl = this.#getMarketplaceResultUrl(item);
      if (!linkUrl) return;

      const card = document.createElement('li');
      card.className = 'marketplace-search-results__card predictive-search-results__card';

      const link = document.createElement('a');
      link.className = 'marketplace-search-results__link';
      link.href = linkUrl;

      const image = document.createElement('img');
      image.className = 'marketplace-search-results__thumb';
      image.loading = 'lazy';
      image.alt = item?.title || 'Marketplace result';
      image.src = Array.isArray(item?.images) && item.images[0] ? item.images[0] : 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

      const body = document.createElement('div');
      body.className = 'marketplace-search-results__body';

      const eyebrow = document.createElement('div');
      eyebrow.className = 'marketplace-search-results__eyebrow';
      eyebrow.textContent =
        item?.catalog_path ||
        item?.catalog_context_path ||
        item?.seller_name ||
        item?.brand ||
        item?.source_label ||
        item?.category_name ||
        'Marketplace listing';

      const name = document.createElement('div');
      name.className = 'marketplace-search-results__name';
      name.textContent = item?.title || 'Untitled listing';

      const price = document.createElement('div');
      price.className = 'marketplace-search-results__price';
      price.textContent = typeof item?.selling_price === 'number' ? `Rs. ${item.selling_price.toLocaleString('en-IN')}` : 'View details';

      const sub = document.createElement('div');
      sub.className = 'marketplace-search-results__sub';
      sub.textContent = [item?.source_label, item?.catalog_label || item?.subcategory, item?.commerce_mode].filter(Boolean).join(' | ') || 'Open listing';

      body.append(eyebrow, name, price, sub);
      link.append(image, body);
      card.append(link);
      list.append(card);
    });

    if (!list.childElementCount) return;

    wrapper.append(list);
    inner.append(wrapper);
  }

  #getMarketplaceResultUrl(item) {
    if (typeof item?.url === 'string' && item.url.trim()) return item.url.trim();
    if (typeof item?.catalog_public_url === 'string' && item.catalog_public_url.trim()) {
      return item.catalog_public_url.trim();
    }
    if (typeof item?.shopify_handle === 'string' && item.shopify_handle.trim()) {
      return `/products/${item.shopify_handle.trim()}`;
    }
    if (typeof item?.public_handle === 'string' && item.public_handle.trim()) {
      return `/apps/marketplace?listing=${encodeURIComponent(item.public_handle.trim())}`;
    }
    return '';
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
